<template>
  <div>
    <div v-if="configuration != null" v-show="!isLoading" :class="rowClass">
      <h4>{{ configuration.ModelName }} {{ configuration.TrimlineName }}</h4>
      <small class="stockcarInfo">
        <span v-if="komNumber">{{ i18n.t('/Configuration/KomNumber') }}: {{ komNumber }}</span>
        <span v-if="configuration.LicensePlate">
          | {{ i18n.t('/Configuration/LicensePlate') }}: {{ configuration.LicensePlate }}</span
        >
        <span v-if="configuration.Vin">
          | {{ i18n.t('/Configuration/Vin') }}: {{ configuration.Vin }}</span
        >
      </small>

      <!-- Basisauto -->
      <quote-list-block>
        <template #header>
          <div class="title">{{ i18n.t('/Configuration/BaseCar') }}</div>
          <div class="code">{{ i18n.t('/Common/Code') }}</div>
          <div
            v-if="showExclPrice"
            class="price price-excl"
            data-qa="configSection-price-excl-header"
          >
            <div>
              {{ i18n.t('/Common/ExclBtw') }}
            </div>
            <price-display-drop-down v-if="!showInclPrice && usePriceDisplayMode" />
          </div>
          <div
            v-if="showInclPrice"
            class="price price-incl"
            data-qa="configSection-price-incl-header"
          >
            <div>
              {{ i18n.t('/Common/InclBtw') }}
            </div>
            <price-display-drop-down v-if="usePriceDisplayMode" />
          </div>
        </template>
        <template #body>
          <configuration-base-car
            :configuration="configuration"
            @update-calculate-bpm="updateCalculateBpm"
          />
        </template>
      </quote-list-block>
      <bpm-incomplete-message />

      <!-- Exterieur -->
      <quote-list-block>
        <template #header>
          <div class="title">{{ i18n.t('/Configuration/Exterior') }}</div>
        </template>
        <template #body>
          <configuration-feature
            :feature="configuration.Color"
            :feature-label="i18n.t('/Configuration/Color')"
            :show-incl="showInclPrice"
            :show-excl="showExclPrice"
          />
          <configuration-feature
            :feature="configuration.Rim"
            :feature-label="i18n.t('/Configuration/Rims')"
            :show-incl="showInclPrice"
            :show-excl="showExclPrice"
          />
        </template>
      </quote-list-block>

      <!-- Interieur -->
      <quote-list-block>
        <template #header>
          <div class="title">{{ i18n.t('/Configuration/Interior') }}</div>
        </template>
        <template #body>
          <configuration-feature
            :feature="configuration.Interior.Upholstery"
            :feature-label="configuration.IsCC2 ? '' : i18n.t('/Configuration/Upholstery')"
            :show-incl="showInclPrice"
            :show-excl="showExclPrice"
          />
          <configuration-feature
            :feature="configuration.Interior.UpholsteryColor"
            :feature-label="i18n.t('/Configuration/Color')"
            :show-incl="showInclPrice"
            :show-excl="showExclPrice"
          />
          <template v-if="interiorOptions">
            <configuration-feature
              v-for="feature in interiorOptions"
              :key="feature.Id"
              :feature="feature"
              :feature-label="i18n.t('/Configuration/Interior Package')"
              :show-incl="showInclPrice"
              :show-excl="showExclPrice"
            />
          </template>
        </template>
      </quote-list-block>

      <!-- Opties & accessoires -->
      <quote-list-block v-if="optionsAndAccessoriesWithoutInterior.length > 0">
        <template #header>
          <div class="title">
            {{ i18n.t('/Configuration/OptionsAndDealerAccessoiries') }}
          </div>
        </template>
        <template #body>
          <configuration-feature
            v-for="feature in optionsAndAccessoriesWithoutInterior"
            :key="feature.Id"
            :feature="feature"
            :show-incl="showInclPrice"
            :show-excl="showExclPrice"
          />
        </template>
      </quote-list-block>

      <quote-list-block>
        <template #header>
          <div class="title">
            {{ i18n.t('/Configuration/ConsumerPriceInclBtwBpm') }}
          </div>
        </template>
        <template #body>
          <li class="row">
            <div v-if="showExclPrice" class="price price-excl">
              {{ i18n.n(configuration.Price.Excl, 'currency') }}
            </div>
            <div v-if="showInclPrice" class="price price-incl" data-qa="configuration-incl-price">
              {{ i18n.n(configuration.Price.Incl, 'currency') }}
            </div>
          </li>
        </template>
      </quote-list-block>
    </div>
    <loader v-if="isLoading" />
  </div>
</template>

<script>
import ConfigurationFeature from '@/components/generic/presentational/organisms/configurationFeature/ConfigurationFeature.vue'
import ConfigurationBaseCar from '@/components/generic/presentational/organisms/configurationBaseCar/ConfigurationBaseCar.vue'
import QuoteListBlock from '@/components/generic/presentational/molecules/quoteListBlock/QuoteListBlock.vue'
import PriceDisplayDropDown from '@/components/generic/presentational/molecules/priceDisplayDropDown/PriceDisplayDropDown.vue'
import StoreConnector from '@/helpers/StoreConnector'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import quoteOrderService from '@/services/quoteOrderService.js'
import { mapGetters } from 'vuex'
import BpmIncompleteMessage from '@/components/generic/presentational/molecules/bpmIncompleteMessage/BpmIncompleteMessage.vue'

export default {
  components: {
    ConfigurationFeature,
    ConfigurationBaseCar,
    Loader,
    QuoteListBlock,
    PriceDisplayDropDown,
    BpmIncompleteMessage,
  },
  provide() {
    return {
      enableNoBpm: this.enableNoBpm,
    }
  },
  props: {
    enableNoBpm: {
      type: Boolean,
      default: false,
    },
    usePriceDisplayMode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      configuration: null,
      komNumber: '',
      isLoading: true,
    }
  },
  computed: {
    ...mapGetters({
      showInclPrice: 'quote/isPriceShowIncl',
      showExclPrice: 'quote/isPriceShowExcl',
    }),
    rowClass() {
      if (!this.showInclPrice) return 'row-price-excl'
      if (!this.showExclPrice) return 'row-price-incl'
      return ''
    },
    optionsAndAccessories() {
      if (this.configuration.Categories.length > 0) {
        let arr = []
        for (let i = 0; i < this.configuration.Categories.length; i++) {
          arr = arr.concat(this.configuration.Categories[i].Features)
        }
        return arr
      } else return []
    },
    interiorOptions() {
      return this.optionsAndAccessories.filter((option) => this.isInteriorControlCode(option?.Id))
    },
    optionsAndAccessoriesWithoutInterior() {
      return this.optionsAndAccessories.filter((option) => !this.isInteriorControlCode(option?.Id))
    },
  },
  created() {
    StoreConnector.get('configuration/configuration', (data) => {
      this.configuration = data
    })
    StoreConnector.get('configuration/komNumber', (data) => {
      this.komNumber = data
    })
    StoreConnector.get('configuration/isLoading', (data) => {
      this.isLoading = data
    })
  },
  methods: {
    updateCalculateBpm(calculateBpm) {
      quoteOrderService.updateCalculateBpm(calculateBpm)
    },
    isInteriorControlCode(code) {
      let intValue = parseInt(code)
      return !isNaN(code) && !isNaN(intValue) && intValue >= 100 && intValue <= 999
    },
  },
}
</script>

<style lang="scss" scoped>
:deep() {
  .row {
    grid-template-columns: auto 4em 8em 8em;
    grid-template-areas: 'title code price-excl price-incl';
  }

  .row-price-incl .row {
    grid-template-columns: auto 4em 8em;
    grid-template-areas: 'title code price-incl';
  }

  .row-price-excl .row {
    grid-template-columns: auto 4em 8em;
    grid-template-areas: 'title code price-excl';
  }

  .row:not(.header) {
    border-bottom: 0;
  }
  .price-incl {
    font-weight: 700;
  }
  .row-price-excl .price-excl {
    font-weight: 700;
  }

  .price-excl,
  .price-incl {
    display: flex;
    justify-content: end;
  }
}

.quote-block {
  margin-bottom: 0em;
}

.stockcarInfo {
  display: block;
  font-weight: 400;
  font-size: 0.875rem;
}
</style>
